import React from "react"
import useSSR from "use-ssr"
import {
	Flex,
	Text,
	Button,
	PersonCard,
	breakpoints,
} from "@capioweb/ui-components"
import {
	StringProperty,
	LinkListProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { BlockCommonLayout } from "../../utils/layout"
import { LocalesContext } from "../../context"
import { HEADER_HEIGHT_S, scale } from "@capioweb/ui-components/src"
import { sendContactDataLayerPush } from "../../utils/piwikPro"

interface StaffCard {
	title: string
	additionalInfo: string
	department: string
	email: string
	firstName: string
	lastName: string
	phoneNumber: string
}

interface CapioNexusStaffBlockProps {
	heading: StringProperty
	cards: StaffCard[]
	anchorId?: StringProperty
}

const CapioNexusStaffBlock: React.FC<CapioNexusStaffBlockProps> = ({
	cards,
	anchorId,
	...rest
}) => {
	const { isServer } = useSSR()

	const { translate, translationKeys } = React.useContext(LocalesContext)

	const [showAll, setShowAll] = React.useState(false)

	const [initialItemLimit, setInitialItemLimit] = React.useState(0)

	const ref = React.useRef(null)

	React.useEffect(() => {
		if (isServer) return

		if (window.innerWidth > breakpoints.l) {
			setInitialItemLimit(6)
		} else {
			setInitialItemLimit(3)
		}
	}, [])

	const scrollToRef = () => {
		window.scrollTo(0, ref.current.offsetTop)
		setTimeout(() => {
			const headerOffset = scale(HEADER_HEIGHT_S)
			const elementPosition = ref.current.getBoundingClientRect().top
			const offsetPosition = elementPosition + window.scrollY - headerOffset

			window.scrollTo({
				top: offsetPosition,
				behavior: "smooth",
			})
		}, 100)
	}

	return (
		cards.length > 0 && (
			<Flex ref={ref}>
				<BlockCommonLayout id={anchorId?.value || null} {...rest}>
					<Flex
						flexDirection={{ _: "column", s: "row" }}
						flexWrap="wrap"
						w={{
							_: 1,
							s: "calc(100% + 24px)",
						}}
					>
						{cards.map((c, i) => (
							<Flex
								flex={{ _: 1, s: "0 0 50%", l: "0 0 33.33%" }}
								flexDirection="column"
								px={{ s: 1.5 }}
								mb={{ _: 2, s: 3 }}
								key={i}
								className={
									!showAll && i >= initialItemLimit && "visually-hidden"
								}
							>
								<PersonCard py={3.25} px={3}>
									<Text
										as="h3"
										fontSize={2.5}
										lineHeight={3}
										fontWeight="bold"
										mb={1}
									>
										{c.firstName && `${c.firstName} `}
										{c.lastName && `${c.lastName}`}
										<br />
										{c.department && c.department}
									</Text>
									<Flex flexDirection="column" w={1}>
										{c.title && <Text mb={0}>{c.title}</Text>}
										{c.additionalInfo && <Text mb={0}>{c.additionalInfo}</Text>}
										{c.email && (
											<Text.Anchor
												href={`mailto:${c.email}`}
												textDecoration="underline"
												mb={0}
												onClick={() =>
													sendContactDataLayerPush("mail", c.email)
												}
											>
												{c.email}
											</Text.Anchor>
										)}
										{c.phoneNumber && (
											<Text.Anchor
												href={`tel:${c.phoneNumber}`}
												textDecoration="underline"
												mb={0}
												onClick={() =>
													sendContactDataLayerPush("phone", c.phoneNumber)
												}
											>
												{c.phoneNumber}
											</Text.Anchor>
										)}
									</Flex>
								</PersonCard>
							</Flex>
						))}
					</Flex>

					{cards.length > initialItemLimit && (
						<Flex>
							<Button
								mt={{ l: 2 }}
								variant="ghost"
								icon={showAll ? "minus" : "plus"}
								textColor="navy"
								onClick={() => {
									setShowAll(!showAll)
									showAll && scrollToRef()
								}}
							>
								{showAll
									? translate(translationKeys.viewLess)
									: translate(translationKeys.viewMore)}
							</Button>
						</Flex>
					)}
				</BlockCommonLayout>
			</Flex>
		)
	)
}

export { CapioNexusStaffBlock }
